/**
 * Per-route third-party impact tracker.
 *
 * `index.html` records a `third-party-tags` PerformanceMeasure once GTM +
 * Yandex.Metrika finish evaluating. This helper reads that measure, plus the
 * transfer size of every script served from a known 3P origin, and reports it
 * to analytics as a `third_party_loaded` event so we can watch cost per route
 * over time (baseline: /, /predictions/today, /high-odds, /guides, /blog).
 *
 * Read-only observation — never triggers additional network work.
 */
import { trackEvent } from "@/lib/analytics";

const THIRD_PARTY_HOSTS = [
  "googletagmanager.com",
  "google-analytics.com",
  "mc.yandex.ru",
  "connect.facebook.net",
  "analytics.tiktok.com",
  "static.ads-twitter.com",
  "snap.licdn.com",
  "news.google.com",
];

interface ThirdPartyBreakdown {
  totalMs: number;
  totalBytes: number;
  byOrigin: Record<string, { ms: number; bytes: number; requests: number }>;
}

function collect(): ThirdPartyBreakdown | null {
  if (typeof performance === "undefined" || !performance.getEntriesByType) return null;

  const byOrigin: ThirdPartyBreakdown["byOrigin"] = {};
  let totalMs = 0;
  let totalBytes = 0;

  const resources = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
  for (const r of resources) {
    const host = THIRD_PARTY_HOSTS.find((h) => r.name.includes(h));
    if (!host) continue;
    const entry = byOrigin[host] ?? { ms: 0, bytes: 0, requests: 0 };
    entry.ms += r.duration || 0;
    entry.bytes += r.transferSize || 0;
    entry.requests += 1;
    byOrigin[host] = entry;
    totalMs += r.duration || 0;
    totalBytes += r.transferSize || 0;
  }

  // Fold in the wall-clock `third-party-tags` measure written by index.html.
  const tag = performance.getEntriesByName("third-party-tags")[0];
  if (tag && tag.duration > totalMs) totalMs = tag.duration;

  return { totalMs, totalBytes, byOrigin };
}

/**
 * Report third-party impact for the current route once idle. Safe to call
 * multiple times per session — it fires at most once per route+load pair.
 */
const reported = new Set<string>();
export function reportThirdPartyImpact(route: string): void {
  if (typeof window === "undefined") return;
  const key = `${route}:${performance.timeOrigin ?? 0}`;
  if (reported.has(key)) return;
  reported.add(key);

  const run = () => {
    const stats = collect();
    const policy = (window as unknown as { __statlignThirdParty?: { allowed: string[]; disabledReason: string | null } }).__statlignThirdParty;
    const disabledReason = policy?.disabledReason ?? null;
    const allowedTags = policy?.allowed?.join(",") ?? "";

    // Emit even when nothing loaded, so we can measure how often the slow-
    // signal disable actually fires per route.
    try {
      trackEvent("third_party_loaded", {
        route,
        total_ms: stats ? Math.round(stats.totalMs) : 0,
        total_kb: stats ? Math.round(stats.totalBytes / 1024) : 0,
        origins: stats ? Object.keys(stats.byOrigin).join(",") : "",
        allowed_tags: allowedTags,
        disabled_reason: disabledReason,
      });
    } catch {
      /* analytics is best-effort */
    }
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.info("[Statlign] third-party impact", route, { stats, allowedTags, disabledReason });
    }
  };


  const w = window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run, { timeout: 5000 });
  } else {
    window.setTimeout(run, 3000);
  }
}
